import { useMutation, useQuery, queryCache } from 'react-query'
import axios from 'axios'
import {
  CategoryColumns,
  ContactGoalProgressResponse,
  MetadataFieldsResponse,
  UserGoalProgress,
  UserPrefsColumnsResponse,
  Contact,
  SearchStats,
} from './constants'
import { useUser } from '../../Permissions'

export const ContactQueueApis = {
  metadataFieldsRoute: '/api/rest/recording/metadatafields',
  allColumnsRoute: '/api/rest/recording/apps/contactqueueApp/field',
  userPrefsColumnsRoute: '/api/rest/ui/prefs/contactqueuefields',
  allQueuedContacts: '/api/rest/recording/contact?expand=metadata&expand=eventCalculations&range=date_range_in_the_past_year&appName=goalApp',
  userContactGoalProgressRoute: '/api/rest/recording/contactGoalProgress?personId=',
  searchStats: '/api/rest/recording/contact?expand=metadata&expand=eventCalculations&range=date_range_in_the_past_year&appName=goalApp&searchStats=true',
  randomTaskRoute: '/api/rest/recording/contact/randomTask',
}

export function useMetadataFieldsQuery() {
  return useQuery({
    queryKey: ['metadataFields'],
    queryFn: async () => {
      const res = await axios.get<MetadataFieldsResponse>(ContactQueueApis.metadataFieldsRoute)
      return Array.isArray(res.data) ? res.data : []
    },
  })
}

export function useUserPrefsColumnsQuery() {
  return useQuery({
    queryKey: ['userPrefsColumns'],
    queryFn: async () => {
      const res = await axios.get<UserPrefsColumnsResponse>(ContactQueueApis.userPrefsColumnsRoute)
      return Array.isArray(res.data.columnData) ? res.data.columnData : []
    },
  })
}

export function useAllColumnsQuery() {
  return useQuery({
    queryKey: ['allColumns'],
    queryFn: async () => {
      const res = await axios.get<CategoryColumns[]>(ContactQueueApis.allColumnsRoute)
      return Array.isArray(res.data) ? res.data : [res.data]
    },
  })
}


// This function fetches all queued contacts, pagination is handled through AgGridReact after getting all contacts.
// This is a brute force solution ideally we would retrieve contacts as the user flips through each pagination page.
// Currently the query defaults to fetch the first 100 contacts.
// To get more contacts we need to add Range=item=x-y to request header.
// to get the total number  of contacts we need to call the searchStats endpoint first. The count tells us how many contacts
// we have in total, and we can calculate how many iterations we need to fetch all contacts.
export function useAllQueuedContacts() {
  return useQuery({
    queryKey: ['allQueuedContacts'],
    queryFn: async () => {
      // Calling this ensures that all goals will have contacts queued for them.
      await axios.get<Contact[]>(ContactQueueApis.randomTaskRoute)
      const searchStatsResponse = await axios.get<SearchStats>(ContactQueueApis.searchStats)
      let iterationCount = 1
      if (searchStatsResponse.data?.count && searchStatsResponse.data.count > 100) {
        iterationCount = Math.ceil(searchStatsResponse.data.count / 100)
      }
      let results: Contact[] = []
      for (let i = 0; i < iterationCount; i++) {
        const start = i * 100
        const end = start + 99
        // The api seems to always return an array, even when there is only one contact.
        const contactResponse = await axios.get<Contact[]>(ContactQueueApis.allQueuedContacts,
          { headers: { Range: `items=${start}-${end}` } })
        results = results.concat(contactResponse.data)
      }
      return results
    },
    config: {
      staleTime: 1000 * 60 * 20,
      cacheTime: 1000 * 60 * 20,
    },
  })
}
export function useUpdateContactQueuePreference() {
  return useMutation(
    async (values: { columnData: CategoryColumns[] }) => {
      const res = await axios.post(ContactQueueApis.userPrefsColumnsRoute, values)
      if (res.status !== 200) {
        throw new Error('Error update contact queue user preference')
      }
      return res.data
    },
    {
      onSuccess: () => {
        queryCache.invalidateQueries(['userPrefsColumns'])
      },
      onError: error => {
        throw new Error('Error update contact queue user preference:' + JSON.stringify({ error }))
      },
    },
  )
}

export function useUserActiveContactQueue() {
  const user = useUser()
  return useQuery<UserGoalProgress[]>({
    queryKey: ['userActiveContactQueue', user?.id],
    queryFn: async () => {
      if (!user?.id) return []
      const res = await axios.get<ContactGoalProgressResponse>(ContactQueueApis.userContactGoalProgressRoute + user?.id)
      return Array.isArray(res.data) ? res.data : [res.data]
    },
    enabled: !!user,
  })
}
